import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Cable, ChevronRight, Phone, Zap, Shield, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAQDataCables = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = [
    {
      q: 'What types of data cables does RSD manufacture?',
      a: 'RSD manufactures a comprehensive range of data cables: USB-A to Type-C (45W-120W), USB-A to Micro USB (2.4A-3A QC), Type-C to Type-C (60W-240W PD), and Type-C to Lightning (20W-30W PD). Available in silicone, braided nylon, and premium metal connector variants.'
    },
    {
      q: 'What is the difference between 45W, 65W, and 120W cables?',
      a: 'The wattage indicates maximum power transfer: 45W cables are standard fast charging (good for most phones), 65W cables support VOOC/Super VOOC (OPPO, Realme, OnePlus), and 120W cables are for flagship devices with max power charging. Higher wattage cables have thicker copper cores and better heat dissipation.'
    },
    {
      q: 'Which cable supports VOOC/Super VOOC charging?',
      a: 'Our DT-15 series (65W) and DT-21 series (100W+) support VOOC and Super VOOC charging protocols. These cables are specifically designed with the pin configuration and power handling required for OPPO/Realme/OnePlus fast charging.'
    },
    {
      q: 'What is the difference between braided and silicone cables?',
      a: 'Braided cables (nylon weave) are more durable, tangle-resistant, and have a premium look - ideal for retail. Silicone cables are softer, more flexible, and have a smooth finish - great for daily use. Both offer same electrical performance; the choice is material preference.'
    },
    {
      q: 'Do RSD cables support data transfer?',
      a: 'Yes, all RSD USB cables support both charging and data transfer. USB 2.0 cables support up to 480 Mbps, while USB 3.0/3.1 cables support up to 5-10 Gbps. Our USB4 cables support up to 40 Gbps for high-speed data transfer.'
    },
    {
      q: 'What makes a cable "fast charging compatible"?',
      a: 'Fast charging cables have: 1) Thicker copper wires (lower resistance), 2) Proper pin configuration for the protocol, 3) Higher amperage capacity, 4) E-Marker chip for 100W+ cables. Cheap cables with thin wires limit charging speed regardless of charger wattage.'
    },
    {
      q: 'What is an E-Marker chip and why is it important?',
      a: 'E-Marker is an electronic chip in USB-C cables that communicates cable specifications to devices. Required for cables carrying 100W+ power or 10Gbps+ data. Without E-Marker, devices may limit power to 60W for safety. All RSD 100W+ cables include E-Marker chips.'
    },
    {
      q: 'How long do RSD data cables last?',
      a: 'RSD cables are tested for 10,000+ bend cycles at the connector joints. With normal use, cables last 2-3 years easily. Our braided cables with reinforced connectors can last even longer. All cables come with 1-year warranty.'
    },
    {
      q: 'What cable lengths are available?',
      a: 'Standard lengths: 1 meter (most popular for daily use), 1.5 meters (desktop/nightstand), 2 meters (flexible positioning), and 3 meters (for specific applications). For OEM orders, custom lengths can be manufactured.'
    },
    {
      q: 'Can I get cables with LED indicators?',
      a: 'Yes, RSD offers cables with LED charging indicators that show charging status. Available in our DT-21 series and can be requested for OEM orders. LED indicators add a premium feel and help users know charging is active.'
    },
    {
      q: 'What is the MOQ for bulk data cable orders?',
      a: 'Minimum Order Quantity for data cables is typically 5,000-10,000 units per SKU. For new distributors, we offer flexible MOQ options. Volume discounts available for orders above 25,000 units. Contact our sales team for exact pricing.'
    },
    {
      q: 'Does RSD provide OEM/private label cables?',
      a: 'Yes, RSD offers complete OEM services: custom branding/logo printing, custom colors, private label packaging, and even custom cable designs. MOQ for OEM typically starts at 5,000 units with 15-20 days production time.'
    },
    {
      q: 'How are RSD cables quality tested?',
      a: 'Every cable batch undergoes: 1) Electrical continuity testing, 2) Fast charging protocol verification, 3) 10,000 bend cycle testing, 4) Connector pull-force testing, 5) Heat resistance testing during charging. Our ISO 9001:2015 facility ensures consistent quality.'
    },
    {
      q: 'Which cable should I choose for iPhone?',
      a: 'For iPhone fast charging (iPhone 8 and later), use Type-C to Lightning cable with 20W+ PD support. Pair with a 20W PD charger for optimal fast charging. RSD offers MFi-design compatible Lightning cables.'
    },
    {
      q: 'Can RSD cables be used with laptops?',
      a: 'Yes, our Type-C to Type-C cables (60W-100W PD) can charge laptops that support USB-C charging. For high-power laptops (above 100W), use our 240W USB4 cables with appropriate chargers.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Data Cable FAQ - USB Cable Questions Answered | RSD Accessories</title>
        <meta name="description" content="Find answers to all your data cable questions - fast charging, VOOC support, cable types, E-Marker chips, and more. Expert answers from India&apos;s leading cable manufacturer." />
        <meta name="keywords" content="data cable faq, usb cable questions, type c cable guide, fast charging cable, vooc cable, braided cable vs silicone, e-marker chip, cable manufacturer faq" />
        <link rel="canonical" href="https://rsdaccessories.com/faq/data-cables" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <div className="bg-neutral-100 py-3">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-neutral-500 hover:text-[#FF4D00]">Home</Link>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              <Link to="/faq" className="text-neutral-500 hover:text-[#FF4D00]">FAQ</Link>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              <span className="text-neutral-900 font-medium">Data Cables</span>
            </div>
          </div>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-br from-neutral-900 to-black py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <Cable className="w-16 h-16 text-[#FF4D00] mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Data Cable FAQs
            </h1>
            <p className="text-xl text-neutral-300 max-w-2xl mx-auto">
              Everything you need to know about USB cables, fast charging, and cable specifications
            </p>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-8 bg-[#FF4D00]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
              <div>
                <Zap className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">45W-120W</div>
                <div className="text-sm text-white/80">Fast Charging Range</div>
              </div>
              <div>
                <Cable className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">30,000+</div>
                <div className="text-sm text-white/80">Daily Production</div>
              </div>
              <div>
                <Shield className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">10,000+</div>
                <div className="text-sm text-white/80">Bend Cycle Tested</div>
              </div>
              <div>
                <CheckCircle className="w-8 h-8 mx-auto mb-2" />
                <div className="font-bold">1 Year</div>
                <div className="text-sm text-white/80">Warranty</div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-8">{faqs.length} Questions About Data Cables</h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow border border-neutral-100 hover:border-[#FF4D00] transition-colors">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3 flex items-start gap-3">
                    <span className="bg-[#FF4D00] text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm">
                      {idx + 1}
                    </span>
                    {faq.q}
                  </h3>
                  <p className="text-neutral-600 ml-11">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Categories */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-center mb-8">Explore Other FAQ Categories</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/faq/mobile-chargers" className="bg-white rounded-xl p-6 border hover:border-[#FF4D00] transition-colors">
                <h3 className="font-bold mb-2">Mobile Charger FAQs</h3>
                <p className="text-sm text-neutral-600">BIS certification, wattage guide, protocols</p>
              </Link>
              <Link to="/faq/manufacturing" className="bg-white rounded-xl p-6 border hover:border-[#FF4D00] transition-colors">
                <h3 className="font-bold mb-2">Manufacturing FAQs</h3>
                <p className="text-sm text-neutral-600">OEM services, MOQ, lead times</p>
              </Link>
              <Link to="/faq/quality-warranty" className="bg-white rounded-xl p-6 border hover:border-[#FF4D00] transition-colors">
                <h3 className="font-bold mb-2">Quality & Warranty FAQs</h3>
                <p className="text-sm text-neutral-600">Testing process, warranty claims</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Order Premium Data Cables?</h2>
            <p className="text-white/90 mb-8">Get wholesale prices on fast charging cables - 45W to 120W</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/shop/data-cables">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00]">
                  View Cable Range
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQDataCables;
